/**
 * Interview Definition — the canonical, declarative source of truth for the
 * epic-request interview (ENG-143).
 *
 * The problem this solves: the browser/mailbox interview (driven by the daemon
 * on a paired machine) used to gather only the 3 REQUIRED structured-description
 * fields (problemStatement, proposedSolution, impactAssessment), while the CLI
 * `request-formulator` agent conducts a much deeper interview covering success
 * metrics, target audience, alternatives, and technical context. That depth gap
 * meant a web-started epic request landed shallower than a CLI-started one.
 *
 * This module is the single declarative definition of EVERY interview section
 * (mirrored from `.github/agents/request-formulator.md`, Stages 1–6). Both the
 * machine/job interview prompt (see `packages/cli/src/utils/agent-invoker.ts`)
 * and the parity test derive from it, so the two can never drift: to add or
 * change a section you edit this file, and the job interview + the test follow.
 *
 * 🔴 Purity: this is pure data plus a pure string builder — no runtime deps, no
 * imports. `@mycompbox/shared` is normally types-only; a declarative const +
 * its type + a deterministic renderer is an acceptable exception (matching the
 * `model-catalog` / `pxpipe` config pattern already in this package).
 */
/**
 * ENG-313 — the allowed values for the optional `databaseChoice` field: what
 * kind of database the employee expects the work to use. This only records the
 * employee's *intent*. The planner never turns it into a vendor or credentials;
 * it plans against a `DATABASE_URL` placeholder (see .github/agents/planner.md).
 *
 *  - `free_hosted`: a free hosted database is fine
 *  - `cloud_paid`:  a paid managed cloud database
 *  - `existing`:    an existing database the team already has
 *  - `none`:        the work does not need a database
 */
export const DATABASE_CHOICE_VALUES = ["free_hosted", "cloud_paid", "existing", "none"];
/**
 * Normalizes a raw `databaseChoice` value from a model reply or a stored
 * request. It accepts small formatting differences (case, surrounding spaces,
 * `-` or spaces in place of `_`). Anything outside the four allowed values
 * returns `undefined`, so the caller drops the value instead of failing. Old
 * requests without the field stay valid.
 */
export function normalizeDatabaseChoice(raw) {
    if (typeof raw !== "string")
        return undefined;
    const candidate = raw.trim().toLowerCase().replace(/[\s-]+/g, "_");
    return DATABASE_CHOICE_VALUES.includes(candidate)
        ? candidate
        : undefined;
}
/**
 * The canonical interview definition — every section the request-formulator
 * covers, in the order it asks them. Derived from
 * `.github/agents/request-formulator.md` Stages 1–6.
 */
export const INTERVIEW_DEFINITION = [
    {
        key: "problemStatement",
        title: "Problem Statement",
        purpose: "Establish the problem or opportunity being addressed, the gap in the current system, who feels it, and the cost of leaving it unsolved.",
        required: true,
        followUps: [
            "What problem are you trying to solve?",
            "What gap exists in the current system?",
            "Who is experiencing this problem?",
            "What's the impact if we don't address it?",
        ],
        fields: ["problemStatement"],
    },
    {
        key: "proposedSolution",
        title: "Proposed Solution",
        purpose: "Capture what they propose building, how it solves the problem, the high-level approach, and the specific capabilities needed.",
        required: true,
        followUps: [
            "What do you propose building?",
            "How would this solve the problem?",
            "What's the high-level approach?",
            "Are there specific features or capabilities needed?",
        ],
        fields: ["proposedSolution"],
    },
    {
        key: "impactAssessment",
        title: "Impact Assessment",
        purpose: "Capture who benefits, the expected benefits, how it improves the system/product/workflow, and what outcomes would indicate success.",
        required: true,
        followUps: [
            "Who will benefit from this?",
            "What are the expected benefits?",
            "How will this improve the system, product, or workflow?",
            "What outcomes would indicate success?",
        ],
        fields: ["impactAssessment"],
    },
    {
        key: "successMetrics",
        title: "Success Metrics",
        purpose: "Define how success is measured — specific metrics, signals it is working, and the definition of done for the epic.",
        required: false,
        followUps: [
            "What specific metrics would you track?",
            "How would we know this is working?",
            "What's the definition of \"done\" for this epic?",
        ],
        fields: ["successMetrics"],
    },
    {
        key: "targetAudience",
        title: "Target Audience",
        purpose: "Identify which users or teams benefit, any specific personas or use cases, and whether they are internal or external.",
        required: false,
        followUps: [
            "Which users or teams will use this?",
            "Are there specific personas or use cases?",
            "Are these internal or external users?",
        ],
        fields: ["targetAudience"],
    },
    {
        key: "technicalContext",
        title: "Technical Context",
        purpose: "Surface dependencies and prerequisites, a rough effort estimate, and any technical constraints or risks.",
        required: false,
        followUps: [
            "Are there dependencies or prerequisites?",
            "What's your rough estimate of effort (e.g., \"2-3 weeks\", \"1 quarter\")?",
            "Are there technical constraints or risks to be aware of?",
        ],
        fields: ["dependencies", "estimatedEffort"],
    },
    {
        key: "alternatives",
        title: "Alternatives Considered",
        purpose: "Capture alternative approaches that were considered and why they were not chosen.",
        required: false,
        followUps: [
            "Are there alternative approaches you considered?",
            "Why were those alternatives not chosen?",
        ],
        fields: ["alternatives"],
    },
    {
        key: "database",
        title: "Database",
        purpose: "Record what kind of database the work should use, as databaseChoice set to exactly one of: free_hosted, cloud_paid, existing, none. Put any extra detail the employee gives in databaseNote. Never ask for, suggest, or record credentials, connection strings, or a specific vendor.",
        required: false,
        followUps: [
            "Will this need a database: a free hosted one, a paid cloud one, an existing one your team already has, or none?",
        ],
        fields: ["databaseChoice", "databaseNote"],
        askOnlyWhen: "the idea involves storing or persisting data. Ask this at most once, and skip it entirely for ideas that store nothing",
    },
];
/** The section keys, in order — convenience for callers and tests. */
export function interviewSectionKeys() {
    return INTERVIEW_DEFINITION.map((s) => s.key);
}
/**
 * The distinct completion-JSON field keys the interview must gather, derived
 * from the definition (union of every section's `fields`, in first-seen order).
 * The machine's interview completion object carries these keys plus `title` and
 * `description`.
 */
export function interviewFieldKeys() {
    const seen = new Set();
    const out = [];
    for (const section of INTERVIEW_DEFINITION) {
        for (const f of section.fields) {
            if (!seen.has(f)) {
                seen.add(f);
                out.push(f);
            }
        }
    }
    return out;
}
/**
 * Minimum word counts enforced (via prompt guidance) on the fields an automated
 * quality rubric scores. Kept here so the numbers travel with the definition
 * rather than being re-hardcoded in the prompt string. `description` is the
 * standalone narrative Epic Request `description` field (not a section).
 */
export const INTERVIEW_FIELD_MIN_WORDS = {
    problemStatement: 60,
    proposedSolution: 100,
    impactAssessment: 40,
    description: 150,
};
/**
 * Renders the full interview system prompt from an interview definition. This
 * is the exact instruction text the machine/daemon runs to conduct the
 * epic-request interview, and it is the canonical "job interview payload" the
 * parity test asserts against. Deterministic and pure: same definition in →
 * same string out.
 *
 * The rendered prompt (a) names the persona, (b) enforces one-question-at-a-time,
 * (c) walks EVERY section (title + purpose + representative follow-ups, marked
 * required/optional), (d) restates the length minimums for scored fields, and
 * (e) specifies the exact completion JSON shape (title + every field key +
 * description).
 *
 * ENG-149 — CONTINUATION MODE: when `seedContext` is supplied (a prior
 * transcript), a resume/enrich header is prepended that (a) shows the earlier
 * conversation, (b) instructs the formulator to ask ONLY about missing or thin
 * sections rather than re-walking every section, and (c) requires the final
 * completion JSON to MERGE prior answers with the new detail so nothing already
 * captured is lost. The no-seed path is byte-identical to before (the parity
 * test calls `buildInterviewSystemPrompt()` with no args), so the base interview
 * contract is unchanged.
 */
export function buildInterviewSystemPrompt(definition = INTERVIEW_DEFINITION, seedContext) {
    const requiredSections = definition.filter((s) => s.required);
    const optionalSections = definition.filter((s) => !s.required);
    const renderSection = (s) => {
        const tag = s.required
            ? "REQUIRED"
            : s.askOnlyWhen
                ? `OPTIONAL — ask ONLY if ${s.askOnlyWhen}`
                : "OPTIONAL — ask, but the user may decline";
        const followUps = s.followUps.map((q) => `    - ${q}`).join("\n");
        return `- ${s.title} (${tag}) [json keys: ${s.fields.join(", ")}]\n  ${s.purpose}\n${followUps}`;
    };
    const completionKeys = [
        "title",
        ...interviewFieldKeysFor(definition),
        "description",
    ];
    const completionShape = completionKeys
        .map((k) => `"${k}": "..."`)
        .join(", ");
    const base = `You are the CompBox request-formulator, interviewing an employee to turn their idea into a well-formed Epic Request. Ask exactly ONE short, focused question at a time — never multiple questions in one message, never preamble or explanation.

Work through these interview sections in order. Cover EVERY required section; for optional sections, ask about them too, but if the employee has nothing to add or declines, move on and leave that field out (never invent or pad it). A section marked "ask ONLY if" is conditional: when its condition does not hold, skip it without asking and leave its fields out:

REQUIRED sections (an Epic Request cannot omit these):
${requiredSections.map(renderSection).join("\n")}

OPTIONAL-BUT-RECOMMENDED sections (ask about each; omit the field if the employee has nothing):
${optionalSections.map(renderSection).join("\n")}

The employee's answers may be short or casual — that's fine, but when you write the final fields below, EXPAND them into complete, well-written prose using everything they told you across the whole conversation, not just a compressed one-liner. Each required field is scored by an automated quality rubric with hard minimums, so undershooting length is a real failure, not just a style nitpick:
- problemStatement: at least ${String(INTERVIEW_FIELD_MIN_WORDS.problemStatement)} words.
- proposedSolution: at least ${String(INTERVIEW_FIELD_MIN_WORDS.proposedSolution)} words.
- impactAssessment: at least ${String(INTERVIEW_FIELD_MIN_WORDS.impactAssessment)} words.
- description: at least ${String(INTERVIEW_FIELD_MIN_WORDS.description)} words. This is a SEPARATE plain-text field (not shown to the employee) that stands alone as a full narrative summary of the whole request — restate the problem, the proposed solution, and the expected impact in flowing prose, as if writing the request from scratch for someone who hasn't seen the conversation.

Once you have gathered the required sections (and covered the optional ones), respond with ONLY a fenced JSON code block (no other text) in exactly this shape. Include a key for every optional field you gathered; omit any optional field the employee had nothing for:
\`\`\`json
{"done": true, ${completionShape}}
\`\`\`

Until then, respond with ONLY your next question as plain text — no JSON, no labels, no numbering.`;
    if (!seedContext || seedContext.priorTranscript.length === 0) {
        return base;
    }
    return `${renderContinuationHeader(seedContext.priorTranscript)}\n\n${base}`;
}
/**
 * ENG-149 — renders the CONTINUATION preamble prepended to the base interview
 * prompt when resuming a prior interview. It shows the earlier conversation and
 * flips the interviewer from "walk every section" to "fill the gaps": don't
 * re-ask what's already answered, target only missing or thin sections, and on
 * completion merge prior + new so no captured content is dropped.
 */
function renderContinuationHeader(priorTranscript) {
    const transcript = priorTranscript
        .map((t) => `${t.role === "agent" ? "You" : "Employee"}: ${t.content}`)
        .join("\n");
    return `⚠️ CONTINUATION — this is NOT a fresh interview. You already interviewed this employee; below is that prior conversation, and an Epic Request already exists from it. Your job now is to ENRICH that same request, not restart it.

Prior conversation:
${transcript}

How to continue:
- Read the prior conversation first. Do NOT re-ask anything already answered there.
- Use the section list below only as a checklist of what a complete request needs. Identify which sections are MISSING or THIN, and ask focused questions to fill ONLY those gaps — still one short question at a time.
- If the prior conversation already covers everything well, go straight to the completion JSON.
- When you emit the final completion JSON, MERGE the prior answers with whatever new detail you gathered — restate and expand every field using the WHOLE conversation (old + new). Never drop or shorten content that was already captured; the goal is a richer request than before, meeting the same length minimums.

`;
}
/**
 * Internal: the field-key union for an arbitrary definition (mirrors
 * {@link interviewFieldKeys} but parameterized so {@link buildInterviewSystemPrompt}
 * stays consistent when handed a custom definition).
 */
function interviewFieldKeysFor(definition) {
    const seen = new Set();
    const out = [];
    for (const section of definition) {
        for (const f of section.fields) {
            if (!seen.has(f)) {
                seen.add(f);
                out.push(f);
            }
        }
    }
    return out;
}
//# sourceMappingURL=interview-definition.js.map